import { debounce } from 'lodash';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Button, Col, Row } from 'reactstrap';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { renderValidationError } from 'utils/util/formatter';
import { isValidGroup } from 'views/Widgets/Filters/AdvancedFilter';
import {
  AdvancedDatasetFilter,
  useAdvancedDatasetFilter,
} from 'views/Widgets/Filters/AdvancedSpecializedFilter';
import { toApiFilter } from 'views/Widgets/Filters/AdvancedSpecializedFilter/model';
import { ApiGroup } from 'views/Widgets/Filters/AdvancedSpecializedFilter/types';
import SwitchInput from 'views/Widgets/Inputs/SwitchInput';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import { useCampaignReports } from '../CampaignReportsContext';
import PeopleEditorOverlay from '../PeopleEditorOverlay';
import {
  ApiResponseAvailableFiltersCount,
  ApiResponseReport,
  CampaignReportItem,
  CampaignReportItemStatus,
  formatCampaignReportDescriptor,
  toCampaignReportCustomItem,
  toUpdateCustomItemsApiPayload,
} from './EngagementSurvey';

interface Props {
  reportId: number;
  isOpen: boolean;
  onClosed: (hasPublishedItems: boolean) => void;
  onCallback: (hasPublishedItems: boolean) => void;
}

const EngagementSurveyReportEditCustomModalEditor: FC<Props> = ({
  reportId,
  isOpen,
  onClosed,
  onCallback,
}) => {
  const { formatMessage } = useIntl();
  const { campaignId, canPublish } = useCampaignReports();
  const proxyParameters = useProxyParameters();

  const [reportData, setReportData] = useState<ApiResponseReport | null>(null);
  const [hasPublishedItems, setHasPublishedItems] = useState(false);
  const [campaignReportItem, setCampaignReportItem] =
    useState<CampaignReportItem | null>(null);
  const [filterCounter, setFilterCounter] = useState<number>(0);
  const [requestFiltersCounter, setRequestFiltersCounter] = useState<
    ApiGroup | undefined
  >(undefined);
  const [showRequestFilerCounterLoader, setShowRequestFilerCounterLoader] =
    useState<boolean>(false);

  const showRequestFilerCounterLoaderDelayedRef = useRef(
    debounce(setShowRequestFilerCounterLoader, 2000)
  );

  const { suggestions, selectedSuggestionConverter } =
    useAdvancedDatasetFilter();

  const onSuccesfullRetrieve = useCallback(
    (
      data: ApiResponseReport | null,
      error: unknown,
      hardErrorMessage: unknown
    ): void => {
      if (data && !error && !hardErrorMessage) {
        const customItem = toCampaignReportCustomItem(
          data,
          suggestions,
          selectedSuggestionConverter
        );
        setReportData(data);
        setCampaignReportItem(customItem);
        setFilterCounter(customItem.count);
      }
    },
    [selectedSuggestionConverter, suggestions]
  );

  const handleFilterCountrRetrievePreSubmit = useCallback(() => {
    showRequestFilerCounterLoaderDelayedRef.current(true);
  }, []);

  const onSuccesfullFiltersCountRetrieve = useCallback(
    (
      data: ApiResponseAvailableFiltersCount | null,
      error: unknown,
      hardErrorMessage: unknown
    ): void => {
      if (data && !error && !hardErrorMessage) {
        setFilterCounter(data.count);
        showRequestFilerCounterLoaderDelayedRef.current.cancel();
        setShowRequestFilerCounterLoader(false);
      }
    },
    [setShowRequestFilerCounterLoader]
  );

  const { error: errorForReportData } = useConfirmApi<ApiResponseReport>({
    url: `/campaigns/${campaignId}/reports/${reportId}`,
    method: 'GET',
    clearOnChange: true,
    clearOnError: true,
    disabled: !isOpen || !suggestions,
    callback: onSuccesfullRetrieve,
  });

  const {
    error: errorForFilterCountRetrieve,
    status: statusForFilterCountRetrieve,
  } = useConfirmApi<ApiResponseAvailableFiltersCount>({
    url: `/campaigns/${campaignId}/available-filters/count`,
    method: 'POST',
    params: {
      filters: requestFiltersCounter,
    },
    clearOnChange: true,
    clearOnError: true,
    disabled: !isOpen || !requestFiltersCounter,
    onPreSubmit: handleFilterCountrRetrievePreSubmit,
    callback: onSuccesfullFiltersCountRetrieve,
  });

  useEffect(() => {
    if (errorForFilterCountRetrieve) {
      toast.error('Error estimating people contributing to the report');
    }
  }, [errorForFilterCountRetrieve]);

  const handleInputsChange = useCallback((_newObject) => {
    // Do nothing
  }, []);

  const handleOnChange = useCallback((newObject: CampaignReportItem) => {
    setCampaignReportItem(newObject);
    if (isValidGroup(newObject?.filters)) {
      setRequestFiltersCounter(toApiFilter(newObject?.filters));
    }
  }, []);

  const handleOnClosed = useCallback(() => {
    onClosed(hasPublishedItems);
    setReportData(null);
  }, [onClosed, hasPublishedItems]);

  const handleOnValueItemChanged = useCallback(
    (id: keyof CampaignReportItem) => (value: unknown) => {
      setCampaignReportItem((item: CampaignReportItem | null) => ({
        ...item!,
        [id]: value,
      }));
    },
    []
  );

  const handleCallback = useCallback(() => {
    onCallback(hasPublishedItems);
  }, [onCallback, hasPublishedItems]);

  const handleOnSubmit = useCallback(
    (submittedObject, onSubmit) => {
      ConfirmAPI.sendRequestToConfirm(
        'POST',
        `/campaigns/${campaignId}/reports/${reportId}/update`,
        toUpdateCustomItemsApiPayload(submittedObject, proxyParameters),
        (data, error: unknown, hardErrorMessage: unknown) => {
          onSubmit(data, error, hardErrorMessage);

          // Check if we have any published items after successful save
          if (!error && !hardErrorMessage && data) {
            // If we have data and items, check if any are published
            const hasPublishedItems = data.items?.some(
              (item) => item.status === CampaignReportItemStatus.PUBLISHED
            );
            setHasPublishedItems(!!hasPublishedItems);
            onCallback(!!hasPublishedItems);
          }
        }
      );
    },
    [campaignId, proxyParameters, reportId, onCallback]
  );

  const renderForm = useCallback(
    (inputs, submitButton) => {
      if (!reportData) {
        return <Loading />;
      }
      const reportInfo = formatCampaignReportDescriptor(
        reportData!.description,
        formatMessage
      );
      return (
        <>
          <Row>
            <Col>
              <h4>{reportInfo.title}</h4>
              <small className="text-muted">
                <FormattedMessage
                  id="app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.report_with_custom_sections_of_the_organization"
                  defaultMessage="Report with custom sections of the organization"
                />
              </small>
            </Col>
          </Row>

          <Row className="mt-4">{inputs}</Row>
          {errorForReportData && (
            <Row>{renderValidationError(errorForReportData)}</Row>
          )}
          <Row className="mt-4">
            <Col>
              <Button className="w-100" color="light" onClick={handleOnClosed}>
                <FormattedMessage
                  id="app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.cancel"
                  defaultMessage="Cancel"
                />
              </Button>
            </Col>
            <Col>{submitButton}</Col>
          </Row>
        </>
      );
    },
    [errorForReportData, formatMessage, handleOnClosed, reportData]
  );

  const inputs = useMemo(() => {
    if (!campaignReportItem) {
      return [
        {
          type: INPUT_TYPES.CUSTOM_INPUT,
          component: (
            <Loading
              message={formatMessage({
                id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.message.fetching_report_configuration',
                defaultMessage: 'Fetching report configuration...',
              })}
            />
          ),
        },
      ];
    }

    const publishedStatusIsDisabled =
      campaignReportItem.status === CampaignReportItemStatus.UNPUBLISHED &&
      campaignReportItem.viewers.length === 0 &&
      campaignReportItem.owners.length === 0;

    return [
      {
        type: INPUT_TYPES.TEXT,
        autoFocus: false,
        name: 'name',
        label: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.name.label',
          defaultMessage: 'Report name',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.name.helper_text',
          defaultMessage: 'Name that will be displayed in the report.',
        }),
      },
      {
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        name: 'filters',
        label: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.filters.label',
          defaultMessage: 'Filters',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.filters.helper_text',
          defaultMessage:
            'Filters that will be applied to the report. Leave empty to apply no filters and display the whole organization.',
        }),
        component: AdvancedDatasetFilter,
      },
      {
        type: INPUT_TYPES.CUSTOM_INPUT,
        component: (
          <>
            <div className="pt-3">
              <small className="text-muted">
                <FormattedMessage
                  id="app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.filters.counter"
                  defaultMessage="<b>{count}</b> {count, plural, one {person} other {people}} in the organization are eligible to contribute to this report."
                  values={{
                    count: filterCounter,
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              </small>
            </div>
            <div style={{ minHeight: '20px' }}>
              <small className="text-muted">
                {showRequestFilerCounterLoader &&
                  statusForFilterCountRetrieve === 'LOADING' && (
                    <Loading
                      small={true}
                      message={formatMessage({
                        id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.filters.counter.loading',
                        defaultMessage:
                          'Calculating the number of people in the organization that will contribute to this report...',
                      })}
                    />
                  )}
              </small>
            </div>
          </>
        ),
      },

      {
        type: INPUT_TYPES.CUSTOM_INPUT,
        name: 'owners',
        label: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.owners.label',
          defaultMessage: 'Owners',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.owners.helper_text',
          defaultMessage: 'People that will own the report.',
        }),
        component: (
          <PeopleEditorOverlay
            selectedPeople={campaignReportItem.owners}
            editTitle={formatMessage({
              id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.owners.editor.edit_title',
              defaultMessage: 'Edit the people that will own the report.',
            })}
            onUpdate={handleOnValueItemChanged('owners')}
            readonly={false}
          />
        ),
      },
      {
        type: INPUT_TYPES.CUSTOM_INPUT,
        name: 'viewers',
        label: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.viewers.label',
          defaultMessage: 'Viewers',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.viewers.helper_text',
          defaultMessage: 'People that will be able to view the report.',
        }),
        component: (
          <PeopleEditorOverlay
            selectedPeople={campaignReportItem.viewers}
            editTitle={formatMessage({
              id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.viewers.editor.edit_title',
              defaultMessage: 'Edit the people that will own the report.',
            })}
            onUpdate={handleOnValueItemChanged('viewers')}
            readonly={false}
          />
        ),
      },
      {
        type: INPUT_TYPES.CUSTOM_INPUT,
        name: 'status',
        label: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.status.label',
          defaultMessage: 'Published',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.form.status.helper_text',
          defaultMessage:
            'Status of the report. You will be able to publish the report once the survey has ended.',
        }),
        component: (
          <div>
            <SwitchInput
              name="status"
              centered={false}
              value={campaignReportItem.status}
              checkedValue={CampaignReportItemStatus.PUBLISHED}
              uncheckedValue={CampaignReportItemStatus.UNPUBLISHED}
              onChange={handleOnValueItemChanged('status')}
              disabled={publishedStatusIsDisabled || !canPublish}
              enableHelp={publishedStatusIsDisabled}
              hoverHelperText={formatMessage({
                id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.table.column.status.hover_helper_text',
                defaultMessage:
                  'Please add viewers or owners to the report to be able to publish it.',
              })}
            />
          </div>
        ),
      },
    ];
  }, [
    campaignReportItem,
    filterCounter,
    formatMessage,
    handleOnValueItemChanged,
    showRequestFilerCounterLoader,
    statusForFilterCountRetrieve,
    canPublish,
  ]);

  const handleOnValidate = useCallback(
    (obj) => {
      // Ensure all published items have owners and viewers.
      const errors = [];
      if (
        obj.status === CampaignReportItemStatus.PUBLISHED &&
        obj.owners.length === 0 &&
        obj.viewers.length === 0
      ) {
        errors['Error'] = formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_custom_modal_editor.error.published_items_must_have_owners_and_viewers',
          defaultMessage:
            'Published reports must have at least either an owner or viewer.',
        });
      }
      return errors;
    },
    [formatMessage]
  );

  return (
    <ModalEditor
      title={formatMessage({
        id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.title.edit_report',
        defaultMessage: 'Edit report',
      })}
      isOpen={isOpen}
      toggle={handleOnClosed}
      object={campaignReportItem}
      inputs={inputs}
      onInputsChange={handleInputsChange}
      onChange={handleOnChange}
      callback={handleCallback}
      renderForm={renderForm}
      submitPromise={handleOnSubmit}
      submitText={formatMessage({
        id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_edit_modal_editor.submitText.save',
        defaultMessage: 'Save',
      })}
      buttonClassName=""
      onClosed={handleOnClosed}
      className="mb-0"
      onValidate={handleOnValidate}
    />
  );
};

export default React.memo(EngagementSurveyReportEditCustomModalEditor);
